import { useTranslation } from "react-i18next";

import type { TransitionMatrix as TransitionData } from "../reportTypes";
import { ASSESSED_ORDER, stateColourVar, stateLabels } from "../stateMeta";

/**
 * Entry × exit transition heatmap (the workbook crosstab). Rendered as a real
 * <table> for screen readers: rows = entry state, columns = exit state, cells =
 * count of whānau assessed at both phases who moved that way.
 *
 * Cell tint encodes the movement (protective = improved, amber = held, risk =
 * regressed) at an opacity scaled to the count, so the diagonal-and-above
 * "flow toward protective" reads at a glance. The count sits on top at full
 * contrast, so the number never depends on the tint.
 */
type Props = {
  transition: TransitionData;
  caption: string;
};

const STATES = ASSESSED_ORDER;

export function TransitionMatrix({ transition, caption }: Props) {
  const { t } = useTranslation();
  const { matrix, total } = transition;

  let max = 0;
  for (const row of matrix) for (const c of row) if (c > max) max = c;

  const cellColour = (entryIdx: number, exitIdx: number): string => {
    if (exitIdx > entryIdx) return stateColourVar("protective");
    if (exitIdx < entryIdx) return stateColourVar("risk");
    return stateColourVar("change");
  };
  const opacity = (count: number): number =>
    count <= 0 || max === 0 ? 0 : 0.15 + 0.7 * (count / max);

  return (
    <figure className="rpt-matrix">
      <table className="rpt-matrix__table">
        <caption className="rpt-matrix__caption">{caption}</caption>
        <thead>
          <tr>
            <td className="rpt-matrix__corner">
              <span className="rpt-matrix__axis">
                {t("reports.matrix.entryAxis")} ↓ /{" "}
                {t("reports.matrix.exitAxis")} →
              </span>
            </td>
            {STATES.map((k) => (
              <th key={k} scope="col" className="rpt-matrix__colhead">
                <span style={{ color: stateColourVar(k) }}>
                  {stateLabels(t, k).en}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {STATES.map((rowK, ei) => (
            <tr key={rowK}>
              <th scope="row" className="rpt-matrix__rowhead">
                <span style={{ color: stateColourVar(rowK) }}>
                  {stateLabels(t, rowK).en}
                </span>
              </th>
              {STATES.map((colK, xi) => {
                const count = matrix[ei][xi];
                return (
                  <td key={colK} className="rpt-matrix__cell">
                    <span
                      className="rpt-matrix__fill"
                      style={{
                        background: cellColour(ei, xi),
                        opacity: opacity(count),
                      }}
                      aria-hidden="true"
                    />
                    <span className="rpt-matrix__count">{count || ""}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <figcaption className="rpt-matrix__total">
        {t("reports.matrix.total", { n: total })}
      </figcaption>
    </figure>
  );
}

export default TransitionMatrix;
